import { format } from 'sql-formatter';

/**
 * Raw-SQL formatting + light schema auto-correction for the console's Format
 * button. The warehouse table is `AuditLogs` with camelCase columns (backend
 * AUDIT_LIST_COLUMNS); users (and the old design mock) tend to type snake_case
 * (`audit_logs`, `status`, `response_ms`). We map the common wrong forms back to
 * the real names, then pretty-print.
 *
 * NOTE: display-only convenience — the backend schema is authoritative, so this
 * map may drift. It corrects identifiers ONLY outside string literals / comments.
 */
const TABLE = 'AuditLogs';

const COLUMNS = [
    'mongoId', 'timestamp', 'method', 'endpoint', 'path', 'statusCode', 'responseTimeMs',
    'subscriptionId', 'userId', 'originalUserId', 'apiSource', 'environment', 'userType',
    'requestStatus', 'errorMessage', 'isMasquarade', 'isPomMaster', 'sampled', 'sampleRate',
    'queryParams', 'requestBody', 'responseBody', 'dbOperations', 'externalApiCalls',
];

const toSnake = (s: string) => s.replace(/[A-Z]/g, (m) => `_${m.toLowerCase()}`);

// lowercase wrong-form → correct identifier
const CORRECTIONS: Record<string, string> = {
    audit_logs: TABLE,
    auditlogs: TABLE,
    // design-mock-isms that aren't a plain snake_case of the real column
    status: 'statusCode',
    response_ms: 'responseTimeMs',
    response_time: 'responseTimeMs',
};
for (const col of COLUMNS) {
    const snake = toSnake(col); // e.g. statusCode -> status_code
    if (snake !== col.toLowerCase()) CORRECTIONS[snake] = col;
}

// Match (and skip) quoted strings + comments; correct bare identifiers only.
const TOKEN_RE = /'(?:\\.|[^'])*'|"(?:\\.|[^"])*"|`[^`]*`|\/\*[\s\S]*?\*\/|--[^\n]*|#[^\n]*|[A-Za-z_][A-Za-z0-9_]*/g;

export const correctIdentifiers = (sql: string): string =>
    sql.replace(TOKEN_RE, (tok) => {
        const c = tok[0];
        // quoted string / backtick identifier / comment → leave untouched
        if (c === "'" || c === '"' || c === '`' || c === '/' || c === '-' || c === '#') return tok;
        return CORRECTIONS[tok.toLowerCase()] || tok;
    });

/**
 * Correct identifiers, then pretty-print as MySQL with upper-cased keywords.
 * If the SQL is too malformed for the formatter to parse, fall back to the
 * corrected (un-prettified) text so the button never throws.
 */
export const formatSql = (sql: string): string => {
    const corrected = correctIdentifiers(sql);
    try {
        return format(corrected, { language: 'mysql', keywordCase: 'upper' });
    } catch {
        return corrected;
    }
};
